/* eslint-disable prettier/prettier */
/* eslint-disable indent */

import {
  flexRender,
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
} from "@tanstack/react-table";
import { Tooltip } from "antd";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useVirtual } from "react-virtual";

import arrowDown from "../../assets/images/arrow-down.svg";
import arrowUp from "../../assets/images/arrow-up.svg";
import { mixpanelActions } from "../../utils/mixpanel";
import { TableHeaderExtrasContext } from "./TableHeaderExtrasContext";
import {
  EmptyStateContainer,
  HeaderCellWithExtras,
  HeaderExtras,
  TableContainer,
  TableElement,
  ThSorted,
  ThUnsorted,
  tooltipStyles,
  rowTooltipStyles,
  Tr,
  ArrowImage,
  ArrowContent,
  isSorted,
} from "./TableSorted.styles";

export type RowFlagsFn<T> = (
  record: T,
  index: number
) => {
  dimmed?: boolean;
};

interface ITableProps {
  columns: ColumnDef<any, any>[];
  data: any[];
  onRowClick?: (rowContent: any) => void;
  /**
   * Message shown when `data` is empty. A node as well as a string so a
   * caller can hand over a richer state — the admin tables pass a spinner
   * plus a label while their request is still in flight.
   */
  emptyStateText?: React.ReactNode;
  highlightColor?: string | string[];
  isAdherence?: boolean;
  getRowFlags?: RowFlagsFn<any>;
  scrollPositionKey?: string;
  sortingStateKey?: string;
  highlightRowId?: number | null;
  onSortedRowsChange?: (ids: number[]) => void;
  /**
   * Controlled-sorting mode. When `sorting` + `onSortingChange` are both
   * provided, the table delegates sort state to the parent (used for
   * server-side sort). If omitted, the table manages sorting internally.
   */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Disables the client-side sort row model (e.g. when the server sorts). */
  manualSorting?: boolean;
  /**
   * Optional hover text shown on each row, reusing the same dark, no-arrow
   * hover modal as the header tooltips. Accepts a static string or a per-row
   * resolver; return `undefined` to skip the tooltip for a given row.
   */
  rowTooltip?: string | ((record: any, index: number) => string | undefined);
}

export function TableSorted({
  columns,
  data,
  emptyStateText,
  onRowClick,
  highlightColor = "white",
  isAdherence,
  getRowFlags,
  scrollPositionKey,
  sortingStateKey,
  highlightRowId,
  onSortedRowsChange,
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
  manualSorting = false,
  rowTooltip,
}: ITableProps) {
  const { t } = useTranslation();

  const isControlled =
    controlledSorting !== undefined && controlledOnSortingChange !== undefined;
  const [internalSorting, setInternalSorting] = useState<SortingState>(() => {
    if (!sortingStateKey) return [];
    const saved = sessionStorage.getItem(sortingStateKey);
    return saved ? JSON.parse(saved) : [];
  });
  const sorting = isControlled ? controlledSorting : internalSorting;
  const setSorting = isControlled
    ? controlledOnSortingChange
    : setInternalSorting;

  function handleColumnVisibility(columns: ColumnDef<any, string>[]) {
    const columnVisibility: Record<string, boolean> = {};

    columns.forEach((column) => {
      if (column.size === 0 && (column as any).accessorKey) {
        columnVisibility[(column as any).accessorKey] = false;
      }
    });

    return columnVisibility;
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    manualSorting,
    onSortingChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(sorting ?? []) : updater;
      setSorting(next);
    },
    state: {
      sorting,
    },
    initialState: {
      columnVisibility: handleColumnVisibility(columns),
    },
  });

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrolledForId = useRef<number | null>(null);
  const { rows } = table.getRowModel();

  // Find the nearest scrollable ancestor (the real scroll container)
  const getScrollParent = useCallback(
    (element: HTMLElement): HTMLElement | null => {
      let parent = element.parentElement;
      while (parent) {
        const { overflowY } = window.getComputedStyle(parent);
        if (overflowY === "auto" || overflowY === "scroll") {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    },
    []
  );

  // Callback ref for highlighted row — scrolls into view when the DOM element appears
  const highlightedRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (!node || highlightRowId == null) return;
      if (scrolledForId.current === highlightRowId) return;

      scrolledForId.current = highlightRowId;
      // Double rAF ensures layout is fully calculated even for large lists (300+ rows)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          node.scrollIntoView({ block: "center", behavior: "smooth" });
        });
      });
    },
    [highlightRowId]
  );

  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: rows.length,
  });

  const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

  // Fallback: restore scroll position when there's no highlighted row
  useLayoutEffect(() => {
    if (highlightRowId != null) return;
    if (!scrollPositionKey || !tableContainerRef.current) return;

    const scrollContainer = getScrollParent(tableContainerRef.current);
    if (!scrollContainer) return;

    const savedPosition = sessionStorage.getItem(scrollPositionKey);
    if (savedPosition) {
      // Consume: remove after restoring so hard refresh doesn't restore stale position
      sessionStorage.removeItem(scrollPositionKey);
      scrollContainer.scrollTop = Number(savedPosition);
    }
  }, [highlightRowId, scrollPositionKey, getScrollParent]);

  // Persist sorting state to sessionStorage
  useEffect(() => {
    if (!sortingStateKey) return;

    if (sorting.length > 0) {
      sessionStorage.setItem(sortingStateKey, JSON.stringify(sorting));
    } else {
      sessionStorage.removeItem(sortingStateKey);
    }
  }, [sorting, sortingStateKey]);

  // Notify parent of sorted row order whenever rows change
  useEffect(() => {
    if (!onSortedRowsChange) return;

    const sortedIds = rows.map((row) => row.original.id);
    onSortedRowsChange(sortedIds);
  }, [rows, onSortedRowsChange]);

  const saveScrollPosition = useCallback(() => {
    if (!scrollPositionKey || !tableContainerRef.current) return;

    const scrollContainer = getScrollParent(tableContainerRef.current);
    if (!scrollContainer) return;

    sessionStorage.setItem(
      scrollPositionKey,
      String(scrollContainer.scrollTop)
    );
  }, [scrollPositionKey, getScrollParent]);

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;

  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const hasData = Boolean(data && data.length);

  let tablePrimaryColor = "white";

  const isColorsArray = Array.isArray(highlightColor);

  const handleTrackColumn = (column: string) => {
    mixpanelActions.track(`User Action: Sort ${column}`);
  };

  if (isColorsArray) {
    let index = 0;
    tablePrimaryColor = highlightColor[index];
    index = +1;
  }

  // Header extras (the admin Refresh control): rendered at the right-hand end
  // of the LAST header cell, in the header's own band. Wrapped so a click on
  // them never reaches a sortable header's toggle.
  const headerExtras = useContext(TableHeaderExtrasContext);
  const withHeaderExtras = (content: ReactNode, isLast: boolean) =>
    isLast && headerExtras ? (
      <HeaderCellWithExtras>
        {content}
        <HeaderExtras
          data-testid="table-header-extras"
          onClick={(event) => event.stopPropagation()}
        >
          {headerExtras}
        </HeaderExtras>
      </HeaderCellWithExtras>
    ) : (
      content
    );

  return (
    <TableContainer
      emptyState={!hasData}
      ref={tableContainerRef}
      isAdherence={isAdherence}
    >
      <TableElement>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, headerIndex) => {
                const sortDirection = header.column.getIsSorted();
                const nextSortingOrder = header.column.getNextSortingOrder();
                const isLastHeader =
                  headerIndex === headerGroup.headers.length - 1;

                return header.column.getCanSort() ? (
                  <ThSorted
                    style={{}}
                    key={header.id}
                    onClick={() => {
                      handleTrackColumn(header.id);
                      header.column.toggleSorting();
                    }}
                  >
                    {withHeaderExtras(
                      header.isPlaceholder ? null : (
                      <Tooltip
                        title={(() => {
                          if (header.column.getCanSort()) {
                            if (header.column.id === "name") {
                              const nextSortingOrder =
                                header.column.getNextSortingOrder();

                              if (nextSortingOrder === "asc") {
                                return t("table_sort_lastNameAZ");
                              }

                              if (nextSortingOrder === "desc") {
                                return t("table_sort_lastNameZA");
                              }

                              return t("table_sort_off");
                            }

                            if (
                              header.column.id === "hospitalId" ||
                              header.column.id === "id" ||
                              header.column.id === "numberOfReads"
                            ) {
                              const nextSortingOrder =
                                header.column.getNextSortingOrder();

                              if (nextSortingOrder === "asc") {
                                return t("table_sort_lowestHighest");
                              }

                              if (nextSortingOrder === "desc") {
                                return t("table_sort_highestLowest");
                              }

                              return t("table_sort_off");
                            }

                            if (
                              header.column.id === "alerts" ||
                              header.column.id === "lastUsed" ||
                              header.column.id === "surveyReceived" ||
                              header.column.id === "signUpDate" ||
                              header.column.id === "lastEdited"
                            ) {
                              const nextSortingOrder =
                                header.column.getNextSortingOrder();

                              if (nextSortingOrder === "asc") {
                                return t("table_sort_newestOldest");
                              }

                              if (nextSortingOrder === "desc") {
                                return t("table_sort_oldestNewest");
                              }

                              return t("table_sort_off");
                            }

                            if (
                              header.column.id === "surveyResult" ||
                              header.column.id === "lastReviewedBy" ||
                              header.column.id === "title" ||
                              header.column.id === "author" ||
                              header.column.id === "wards"
                            ) {
                              const nextSortingOrder =
                                header.column.getNextSortingOrder();

                              if (nextSortingOrder === "asc") {
                                return t("table_sort_az");
                              }

                              if (nextSortingOrder === "desc") {
                                return t("table_sort_za");
                              }

                              return t("table_sort_off");
                            }
                          }

                          return undefined;
                        })()}
                        zIndex={3002}
                        color="#4B4B4B"
                        arrow={false}
                        placement="top"
                        styles={{
                          body: tooltipStyles,
                        }}
                      >
                        <ArrowContent
                          className={sortDirection ? isSorted() : ""}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {sortDirection === "asc" && (
                            <ArrowImage src={arrowUp} />
                          )}
                          {sortDirection === "desc" && (
                            <ArrowImage src={arrowDown} />
                          )}
                          {/*
                            Not sorted yet: show the direction the next click
                            will apply, faded in only while the header is
                            hovered (see the `preview` variant).
                          */}
                          {!sortDirection && (
                            <ArrowImage
                              preview
                              src={
                                nextSortingOrder === "desc"
                                  ? arrowDown
                                  : arrowUp
                              }
                            />
                          )}
                        </ArrowContent>
                      </Tooltip>
                      ),
                      isLastHeader
                    )}
                  </ThSorted>
                ) : (
                  <ThUnsorted style={{}} key={header.id}>
                    {withHeaderExtras(
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      ),
                      isLastHeader
                    )}
                  </ThUnsorted>
                );
              })}
            </tr>
          ))}
        </thead>

        {hasData && (
          <tbody>
            <tr style={{ backgroundColor: tablePrimaryColor }}>
              <td aria-label="paddingTop" style={{ height: `$2px` }} />
            </tr>
            {paddingTop > 0 && (
              <tr style={{ backgroundColor: tablePrimaryColor }}>
                <td
                  aria-label="customPaddingTop"
                  style={{ height: `${paddingTop}px` }}
                />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index];
              const flags = getRowFlags?.(row.original, virtualRow.index) ?? {};

              const handleOnRowClick = (e: React.MouseEvent) => {
                if (!onRowClick) return;

                // Prevent bubble to PageLayout so it doesn't clear the highlight
                e.stopPropagation();
                saveScrollPosition();
                onRowClick(row.original);
              };

              const isHighlighted =
                highlightRowId != null &&
                Number(row.original.id) === highlightRowId;

              const rowTooltipText =
                typeof rowTooltip === "function"
                  ? rowTooltip(row.original, virtualRow.index)
                  : rowTooltip;

              const rowElement = (
                <Tr
                  ref={isHighlighted ? highlightedRowRef : undefined}
                  onClick={handleOnRowClick}
                  tabIndex={0}
                  key={row.id}
                  dimmed={Boolean(flags.dimmed)}
                  highlighted={isHighlighted}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isActionCell = cell.id.includes("action");
                    // A column whose cell renderer must run even for an empty
                    // value (an inline editor, say) opts out of the "-"
                    // placeholder with meta.renderEmpty — otherwise a cleared
                    // value could never be edited back.
                    const rendersEmpty = Boolean(
                      (
                        cell.column.columnDef.meta as
                          | { renderEmpty?: boolean }
                          | undefined
                      )?.renderEmpty
                    );
                    const cellValue = cell.getValue();

                    return (
                      <td key={cell.id}>
                        {!isActionCell &&
                        !rendersEmpty &&
                        (cellValue == null ||
                          cellValue === "" ||
                          (typeof cellValue === "string" &&
                            cellValue.trim() === ""))
                          ? "-"
                          : flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                      </td>
                    );
                  })}
                </Tr>
              );

              // Reuse the same dark, no-arrow hover modal as the header
              // tooltips, shown per row rather than over the whole table.
              return rowTooltipText ? (
                <Tooltip
                  key={row.id}
                  title={rowTooltipText}
                  color="#4B4B4B"
                  arrow={false}
                  placement="top"
                  styles={{ root: { maxWidth: "none" }, body: rowTooltipStyles }}
                >
                  {rowElement}
                </Tooltip>
              ) : (
                rowElement
              );
            })}

            {paddingBottom > 0 && (
              <tr style={{ backgroundColor: tablePrimaryColor }}>
                <td
                  aria-label="customPaddingBottom"
                  style={{ height: `${paddingBottom}px` }}
                />
              </tr>
            )}
          </tbody>
        )}

        {hasData && <tfoot />}
      </TableElement>

      {!hasData && (
        <EmptyStateContainer>
          <p>{emptyStateText || t("common_noDataToShow")}</p>
        </EmptyStateContainer>
      )}
    </TableContainer>
  );
}
